import * as React from 'react'
import { useCallback, useContext, useMemo, useState } from 'react'

import { MobXProviderContext, observer } from 'mobx-react'

import { PropertyType } from '~/client/graph/types.generated'
import MobileInitialState from '~/client/src/mobile/stores/MobileInitialState'
import {
  ICardConfiguration,
  IResourceRecord,
  ResolveResourceCardLeadingSlot,
  ResolveResourceCardMetaItem,
  ResolveResourceCardSubtitle,
} from '~/client/src/shared/components/ResourceCardList/types'
import ViewMode from '~/client/src/shared/components/ViewSelector/ViewMode'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import { IFilterField } from '~/client/src/shared/hooks/useFilters/types'
import { useFilters } from '~/client/src/shared/hooks/useFilters/useFilters'
import {
  getResourceQuickFilterDefinitions,
  IQuickFilters,
} from '~/client/src/shared/hooks/useResourceQuickFilters'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import UserProject from '~/client/src/shared/models/UserProject'
import {
  buildMobileGroupingOptions,
  buildMobileSortOptions,
} from '~/client/src/shared/resources/builders/buildMobileResourceHelpers'
import { buildResourceListProperties } from '~/client/src/shared/resources/builders/buildResourceListProperties'
import { useResourceFieldCatalog } from '~/client/src/shared/resources/catalog/useResourceFieldCatalog'
import {
  getResourceDefinition,
  IResourceRuntimeContext,
} from '~/client/src/shared/resources/definitions'
import {
  getDefaultCardTemplate,
  type ResourceSchema,
} from '~/client/src/shared/resources/domain/resourceSchema'
import { buildResourceGroupConfig } from '~/client/src/shared/resources/grouping/buildResourceGroupConfig'
import {
  buildResourceCardConfiguration,
  USE_DEFAULT_CARD_CONFIG,
} from '~/client/src/shared/resources/presentation/buildResourceCardConfiguration'
import { useResourceFieldProjector } from '~/client/src/shared/resources/projection/useResourceFieldProjector'
import { UNASSIGNED_FILTER_VALUE } from '~/client/src/shared/utils/ZoneLevelLocationConstants'

import {
  MobileResourceActions,
  MobileResourceContext,
  MobileResourceMeta,
  MobileResourceState,
} from './context'

export interface MobileResourceRecordsResult<TRecord> {
  records: TRecord[]
  loading: boolean
  schema: ResourceSchema
}

export interface MobileResourceConfig<TRecord = any> {
  resourceTypeId: string
  title: string
  icon: React.ComponentType<{ className?: string; size?: string | number }>
  useRecords: (params: {
    projectId?: string
  }) => MobileResourceRecordsResult<TRecord>
  initialSortKey?: string | null
  initialSortOrder?: SortOrder
  initialGroupByKey?: string | null
  resolveMetaItem?: ResolveResourceCardMetaItem
  resolveSubtitle?: ResolveResourceCardSubtitle
  resolveLeadingSlot?: ResolveResourceCardLeadingSlot
  countLabel?: (count: number) => string
  viewModes?: ViewMode[]
  noneGroupByValue?: string
  emptyMessage?: string
  runtimeContext: IResourceRuntimeContext
}

interface MobileResourceProviderProps {
  config: MobileResourceConfig
  state?: MobileInitialState
  children: React.ReactNode
}

const MobileResourceProviderComponent: React.FC<
  MobileResourceProviderProps
> = ({ config, state, children }) => {
  const projectId = state?.activeProject?.id
  const noneGroupByValue = config.noneGroupByValue || 'none'
  const initialSortKey = config.initialSortKey ?? null
  const initialSortOrder = config.initialSortOrder || SortOrder.ASC
  const initialGroupByKey =
    config.initialGroupByKey === noneGroupByValue
      ? null
      : (config.initialGroupByKey ?? null)

  const stores = useContext(MobXProviderContext) as any
  const currentUserId: string | undefined = stores.state?.user?.id
  const currentUserCompanyId = UserProject.getCompanyId(
    stores.state?.user,
    stores.userProjectsStore,
  )

  const {
    records: rawRecords,
    loading: recordsLoading,
    schema,
  } = config.useRecords({ projectId })

  const records = useMemo(
    () => (rawRecords || []) as IResourceRecord[],
    [rawRecords],
  )

  const propertyDefinitions = useMemo(
    () => buildResourceListProperties(config.resourceTypeId, schema.properties),
    [config.resourceTypeId, schema.properties],
  )
  const resourceDefinition = useMemo(
    () => getResourceDefinition(config.resourceTypeId),
    [config.resourceTypeId],
  )
  const userPropertyKeys = useMemo(
    () =>
      propertyDefinitions
        .filter(p => p.propertyType === PropertyType.User)
        .map(p => p.name),
    [propertyDefinitions],
  )
  const companyPropertyKeys = useMemo(
    () =>
      propertyDefinitions
        .filter(p => p.propertyType === PropertyType.Company)
        .map(p => p.name),
    [propertyDefinitions],
  )

  const quickFilterDefinitions = useMemo(
    () =>
      getResourceQuickFilterDefinitions({
        resourceTypeId: config.resourceTypeId,
        userPropertyKeys,
        companyPropertyKeys,
        currentUserId,
        currentUserCompanyId,
      }),
    [
      config.resourceTypeId,
      userPropertyKeys,
      companyPropertyKeys,
      currentUserId,
      currentUserCompanyId,
    ],
  )

  const template = USE_DEFAULT_CARD_CONFIG
    ? undefined
    : getDefaultCardTemplate(schema.templates)

  const cardConfiguration: ICardConfiguration = useMemo(
    () => buildResourceCardConfiguration(resourceDefinition, template),
    [resourceDefinition, template?.cardConfiguration],
  )

  const properties = useMemo(() => {
    return propertyDefinitions.map(prop => ({
      name: prop.name,
      displayName: prop.displayName,
      propertyType: prop.propertyType as PropertyType,
      configuration: prop.configuration
        ? {
            multiline: prop.configuration.multiline || undefined,
            options: prop.configuration.options?.map(opt => ({
              value: opt.value,
              label: opt.label,
              parentValue: opt.parentValue ?? undefined,
            })),
            format: prop.configuration.format || undefined,
            unit: prop.configuration.unit || undefined,
            resourceLink: prop.configuration.resourceLink
              ? {
                  targetResourceType:
                    prop.configuration.resourceLink.targetResourceType,
                }
              : undefined,
          }
        : undefined,
    }))
  }, [propertyDefinitions])

  const fieldCatalog = useResourceFieldCatalog({
    resourceTypeId: config.resourceTypeId,
    properties: propertyDefinitions,
  })
  const fieldProjector = useResourceFieldProjector({
    catalog: fieldCatalog,
    resourceTypeId: config.resourceTypeId,
    runtimeContext: config.runtimeContext,
  })
  const groupingOptions = useMemo(
    () => buildMobileGroupingOptions(fieldCatalog),
    [fieldCatalog],
  )
  const sortingOptions = useMemo(
    () => buildMobileSortOptions(fieldCatalog),
    [fieldCatalog],
  )
  const getDisplayValue = useCallback(
    (propertyName: string, value: any) =>
      fieldProjector.fromValue(propertyName, value).displayText,
    [fieldProjector],
  )

  const filterFields: IFilterField<IResourceRecord>[] = useMemo(() => {
    const fields: IFilterField<IResourceRecord>[] = [
      {
        key: 'name',
        label: Localization.translator.name,
        accessor: record => record.name,
        isSearchable: true,
      },
    ]

    const searchableKeys = new Set(
      fieldCatalog.searchableFields.map(field => field.key),
    )
    const filterableKeys = new Set(
      fieldCatalog.filterableFields.map(field => field.key),
    )
    const behaviorFields = [
      ...fieldCatalog.filterableFields,
      ...fieldCatalog.searchableFields.filter(
        field => !filterableKeys.has(field.key),
      ),
    ]

    behaviorFields.forEach(field => {
      if (field.key === 'name') {
        return
      }

      const isFacetable = field.capabilities.filterable
      const isSearchable = searchableKeys.has(field.key)

      if (!isFacetable && !isSearchable) {
        return
      }

      const hierarchy = isFacetable ? field.hierarchy : undefined

      fields.push({
        key: field.key,
        label: field.label,
        accessor: record => {
          if (!isFacetable) {
            return undefined
          }

          const optionIds = fieldProjector
            .resolve(record, field.key)
            .filterEntries.map(entry => entry.id)
          return optionIds.length === 1 ? optionIds[0] : optionIds
        },
        searchAccessor: isSearchable
          ? record => {
              const projection = fieldProjector.resolve(record, field.key)
              return projection.searchText || undefined
            }
          : undefined,
        displayFormatter: isFacetable
          ? value =>
              fieldProjector.fromEntryId(
                field.key,
                value == null ? UNASSIGNED_FILTER_VALUE : String(value),
              ).label
          : undefined,
        getOptionParentValue: hierarchy
          ? optionId => hierarchy.getParentValue(optionId)
          : undefined,
        getOptionLabel: hierarchy
          ? optionId => hierarchy.getOwnLabel(optionId)
          : undefined,
        isFacetable,
        isSearchable,
      })
    })

    quickFilterDefinitions.forEach(definition => {
      fields.push({
        key: definition.key,
        label: definition.label,
        accessor: record =>
          definition.matchesRecord(record) ? [definition.optionId] : [],
        isFacetable: true,
        isSearchable: false,
        isHiddenInFilterUI: true,
      })
    })

    return fields
  }, [
    fieldCatalog.filterableFields,
    fieldCatalog.searchableFields,
    fieldProjector,
    quickFilterDefinitions,
  ])

  const {
    filteredItems,
    searchKey,
    setSearchKey,
    appliedFilters,
    setFilter,
    getFacetOptions,
    facets,
    resetAll,
  } = useFilters({
    items: records,
    fields: filterFields,
  })

  const quickFilters: IQuickFilters = useMemo(() => {
    const meDefinition = quickFilterDefinitions[0]
    const myCompanyDefinition = quickFilterDefinitions[1]

    const toggleQuickFilter = (key: string, optionId: string) => {
      const isSelected = appliedFilters[key]?.has(optionId)
      setFilter(key, isSelected ? [] : [optionId])
    }

    return {
      isOnlyMe: !!appliedFilters[meDefinition.key]?.has(meDefinition.optionId),
      isOnlyMyCompany: !!appliedFilters[myCompanyDefinition.key]?.has(
        myCompanyDefinition.optionId,
      ),
      toggleOnlyMe: () =>
        toggleQuickFilter(meDefinition.key, meDefinition.optionId),
      toggleOnlyMyCompany: () =>
        toggleQuickFilter(
          myCompanyDefinition.key,
          myCompanyDefinition.optionId,
        ),
      hasUserProperties: meDefinition.isAvailable,
      hasCompanyProperties: myCompanyDefinition.isAvailable,
      resetQuickFilters: () => {
        setFilter(meDefinition.key, [])
        setFilter(myCompanyDefinition.key, [])
      },
    }
  }, [appliedFilters, setFilter, quickFilterDefinitions])

  const selectedFilterCount = useMemo(() => {
    return Object.values(appliedFilters).reduce((sum, set) => sum + set.size, 0)
  }, [appliedFilters])

  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null)
  const [isFilterShown, setIsFilterShown] = useState(false)
  const [selectedGrouping, setGroupingState] = useState<string | null>(
    initialGroupByKey,
  )
  const [selectedSortKey, setSortKey] = useState<string | null>(initialSortKey)
  const [selectedSortOrder, setSortOrder] =
    useState<SortOrder>(initialSortOrder)
  const grouping = groupingOptions.some(
    option => option.key === selectedGrouping,
  )
    ? selectedGrouping
    : null
  const sortKey =
    selectedSortKey === null ||
    sortingOptions.some(option => option.key === selectedSortKey)
      ? selectedSortKey
      : sortingOptions.some(option => option.key === initialSortKey)
        ? initialSortKey
        : null
  const sortOrder =
    sortKey === selectedSortKey ? selectedSortOrder : initialSortOrder

  const groupConfig = useMemo(() => {
    if (!grouping) return undefined

    return buildResourceGroupConfig({
      fieldProjector,
      grouping,
      hierarchy: fieldCatalog.getField(grouping)?.hierarchy,
    })
  }, [fieldProjector, fieldCatalog, grouping])

  const sortedRecords = useMemo(() => {
    if (!sortKey) return filteredItems

    return fieldProjector.sortRecords(filteredItems, sortKey, sortOrder)
  }, [fieldProjector, filteredItems, sortKey, sortOrder])

  const openFilter = useCallback(() => {
    setIsFilterShown(true)
  }, [])

  const closeFilter = useCallback(() => {
    setIsFilterShown(false)
  }, [])

  const selectRecord = useCallback((id: string) => setSelectedRecordId(id), [])

  const closeEdit = useCallback(() => setSelectedRecordId(null), [])

  const setGrouping = useCallback(
    (value: string | null) => {
      if (value === null || value === noneGroupByValue) {
        setGroupingState(null)
      } else {
        setGroupingState(value)
      }
    },
    [noneGroupByValue],
  )

  const loading =
    schema.propertiesLoading || (recordsLoading && records.length === 0)

  const resourceState: MobileResourceState = useMemo(
    () => ({
      records,
      filteredRecords: sortedRecords,
      properties,
      cardConfiguration,
      getDisplayValue,
      resolveMetaItem: config.resolveMetaItem,
      resolveSubtitle: config.resolveSubtitle,
      resolveLeadingSlot: config.resolveLeadingSlot,
      groupConfig,
      searchKey,
      appliedFilters,
      isFilterShown,
      grouping,
      sortKey,
      sortOrder,
      loading,
      selectedFilterCount,
      selectedRecordId,
    }),
    [
      records,
      sortedRecords,
      properties,
      cardConfiguration,
      getDisplayValue,
      config.resolveMetaItem,
      config.resolveSubtitle,
      config.resolveLeadingSlot,
      groupConfig,
      searchKey,
      appliedFilters,
      isFilterShown,
      grouping,
      sortKey,
      sortOrder,
      loading,
      selectedFilterCount,
      selectedRecordId,
    ],
  )

  const resourceActions: MobileResourceActions = useMemo(
    () => ({
      setSearchKey,
      openFilter,
      closeFilter,
      setGrouping,
      setSortKey,
      setSortOrder,
      setFilter,
      resetAll,
      selectRecord,
      closeEdit,
    }),
    [
      setSearchKey,
      openFilter,
      closeFilter,
      setGrouping,
      setSortKey,
      setSortOrder,
      setFilter,
      resetAll,
      selectRecord,
      closeEdit,
    ],
  )

  const resourceMeta: MobileResourceMeta = useMemo(
    () => ({
      resourceTypeId: config.resourceTypeId,
      title: config.title,
      icon: config.icon,
      viewModes: config.viewModes || [],
      countLabel: config.countLabel,
      emptyMessage: config.emptyMessage,
      facets,
      getFacetOptions,
      groupingOptions,
      sortingOptions,
      initialSortKey,
      initialSortOrder,
      runtimeContext: config.runtimeContext,
      quickFilters,
    }),
    [
      config.resourceTypeId,
      config.title,
      config.icon,
      config.viewModes,
      config.countLabel,
      config.emptyMessage,
      facets,
      getFacetOptions,
      groupingOptions,
      sortingOptions,
      initialSortKey,
      initialSortOrder,
      config.runtimeContext,
      quickFilters,
    ],
  )

  const contextValue = useMemo(
    () => ({
      state: resourceState,
      actions: resourceActions,
      meta: resourceMeta,
    }),
    [resourceState, resourceActions, resourceMeta],
  )

  return (
    <MobileResourceContext.Provider value={contextValue}>
      {children}
    </MobileResourceContext.Provider>
  )
}

export const MobileResourceProvider = observer(MobileResourceProviderComponent)
